import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

interface Tenant {
  id: string;
  name: string;
  slug: string;
  active: boolean;
  plataforma?: string;
  idPlataforma?: string;
  platformId?: string | null;
  platform?: {
    id: string;
    name: string;
    value: string;
    url: string;
    active: boolean;
  } | null;
  allowWhatsWeb?: boolean;
  allowOfficialApi?: boolean;
}

interface TenantContextType {
  tenants: Tenant[];
  selectedTenantId: string;
  selectedTenant: Tenant | null;
  setSelectedTenantId: (id: string) => void;
  loading: boolean;
  refreshTenants: () => Promise<void>;
  canUseWhatsWeb: () => boolean;
  canUseOfficialApi: () => boolean;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export function TenantProvider({ children }: { children: ReactNode }) {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  // Helper para fazer requisições autenticadas
  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const savedTenantId = localStorage.getItem('selected_tenant_id');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    // Adicionar tenant ID no header para SuperAdmin
    if (savedTenantId) {
      (headers as Record<string, string>)['X-Tenant-Id'] = savedTenantId;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  };

  const loadTenants = async () => {
    if (user?.role !== 'SUPERADMIN') {
      // Para usuários ADMIN/USER, usar o tenantId do próprio usuário
      if (user?.tenantId) {
        setSelectedTenantId(user.tenantId);
        localStorage.setItem('selected_tenant_id', user.tenantId);
        
        // Buscar dados do tenant atual para obter as permissões
        try {
          const response = await authenticatedFetch('/api/user-tenants/current');
          if (response.ok) {
            const data = await response.json();
            setCurrentTenant(data.tenant);
          }
        } catch (error) {
          console.error('Erro ao carregar dados do tenant:', error);
        }
      } else {
        // Se não tem tenantId, limpar localStorage antigo
        localStorage.removeItem('selected_tenant_id');
      }
      setLoading(false);
      return;
    }

    try {
      const response = await authenticatedFetch('/api/tenants');
      if (response.ok) {
        const data = await response.json();
        const tenantsArray = data.tenants || [];
        setTenants(tenantsArray);

        // Carregar tenant selecionado do localStorage ou selecionar primeiro
        const savedTenantId = localStorage.getItem('selected_tenant_id');
        if (savedTenantId && tenantsArray.some((t: Tenant) => t.id === savedTenantId)) {
          setSelectedTenantId(savedTenantId);
        } else {
          // TenantId salvo é inválido, limpar e selecionar o primeiro
          if (savedTenantId) {
            console.warn('TenantId salvo no localStorage é inválido, limpando:', savedTenantId);
            localStorage.removeItem('selected_tenant_id');
          }

          if (tenantsArray.length > 0) {
            setSelectedTenantId(tenantsArray[0].id);
            localStorage.setItem('selected_tenant_id', tenantsArray[0].id);
          }
        }
      }
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
      toast.error('Erro ao carregar empresas');
    } finally {
      setLoading(false);
    }
  };

  const refreshTenants = async () => {
    setLoading(true);
    await loadTenants();
  };

  useEffect(() => {
    loadTenants();
  }, [user]);

  // Salvar tenant selecionado no localStorage
  useEffect(() => {
    if (selectedTenantId) {
      localStorage.setItem('selected_tenant_id', selectedTenantId);
    }
  }, [selectedTenantId]);

  const selectedTenant = tenants.find(t => t.id === selectedTenantId) || currentTenant || null;

  // Helper functions to check permissions
  const canUseWhatsWeb = () => {
    if (!selectedTenant) return true; // Default to true if no tenant is loaded yet
    return selectedTenant.allowWhatsWeb !== false; // Default to true if undefined
  };

  const canUseOfficialApi = () => {
    if (!selectedTenant) return true; // Default to true if no tenant is loaded yet
    return selectedTenant.allowOfficialApi !== false; // Default to true if undefined
  };

  const value: TenantContextType = {
    tenants,
    selectedTenantId,
    selectedTenant,
    setSelectedTenantId,
    loading,
    refreshTenants,
    canUseWhatsWeb,
    canUseOfficialApi,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant deve ser usado dentro de um TenantProvider');
  }
  return context;
}
